import { useEffect, useState } from "react";
import EditableRow from "./EditableRow";
import CustomButtonSecondary from "../../shared/atomic_components/button/CustomButtonSecondary";
import { CatalogService } from "../../services/controllers/catalogs-services/CatalogsService";
import CustomButton from "../../shared/atomic_components/button/CustomButton";

interface RecordItem {
  id: number;
  name: string;
  rank: number;
  active?: boolean;
  pendingRemoval?: boolean;
  pendingSort?: boolean;
}

interface ConfigTabProps {
  title: string;
  tableName: string;
  notificationFunctions: any;
}

const ConfigTab = ({ title, tableName, notificationFunctions }: ConfigTabProps) => {
  const [editable, setEditable] = useState(false);
  const [records, setRecords] = useState<RecordItem[]>([]);
  const [originalRecords, setOriginalRecords] = useState<RecordItem[]>([]);
  const catalogueService: CatalogService = new CatalogService();

  useEffect(() => {
    const catalogueList = JSON.parse(sessionStorage.getItem("catalogueList") || "{}");
    const recordList = catalogueList ? catalogueList[tableName] : [];

    if (Array.isArray(recordList)) {
      setRecords(recordList);
      setOriginalRecords(recordList);
    }
  }, [tableName]);

  const handleSave = async () => {
    const addedRecords = records.filter(
      (r) => !originalRecords.some((o) => o.id === r.id)
    ).map((r) => ({
      ...r,
      id: null,
      active: true,
    }));;

    const modifiedRecords = records.filter((r) => {
      const orig = originalRecords.find((o) => o.id === r.id);
      return (
        (orig && (orig.name !== r.name || orig.rank !== r.rank)) ||
        r.pendingSort === true
      );
    });

    const removedRecords = originalRecords
      .filter((o) => {
        const current = records.find((r) => r.id === o.id);
        return !current || current.pendingRemoval;
      })
      .map((r) => ({
        ...r,
        active: false,
      }));

    const payload = [...addedRecords, ...modifiedRecords, ...removedRecords].sort(
      (a,b) => (a.id ?? 0) - (b.id ?? 0)
    );

    let response = await catalogueService.updateCatalog(tableName, payload);
    if(response && response.name !== 'AxiosError'){

      const catalogueList = JSON.parse(sessionStorage.getItem("catalogueList") || "{}");
      catalogueList[tableName] = response;
      sessionStorage.setItem("catalogueList", JSON.stringify(catalogueList));

      setRecords(response);
      setOriginalRecords(response);
      setEditable(false);
    }
    else{
      notificationFunctions.setErrorMessage(
        "Errore durante l'aggiornamento, riprovare più tardi"
      );
    }
  };

  const handleCancel = () => {
    setRecords(originalRecords);
    setEditable(false);
  };

  const handleAddRecord = () => {
    setRecords((prev) => {
      const newRank =
        prev.length > 0 ? Math.max(...prev.map((r) => r.rank)) + 1 : 1;
      const newRecord: RecordItem = {
        id: Date.now(),
        name: "",
        rank: newRank,
        pendingRemoval: false,
        pendingSort: false,
      };
      return [...prev, newRecord];
    });
  };

  const moveRecord = (id: number, direction: "up" | "down") => {
    setRecords((prev) => {
      const sorted = [...prev].sort((a, b) => a.rank - b.rank);
      const index = sorted.findIndex((r) => r.id === id);
      if (index === -1) return prev;

      const targetIndex = direction === "up" ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= sorted.length) return prev;

      const updated = [...sorted];

      const tempRank = updated[index].rank;
      updated[index].rank = updated[targetIndex].rank;
      updated[targetIndex].rank = tempRank;

      updated[index] = { ...updated[index], pendingSort: true };
      updated[targetIndex] = { ...updated[targetIndex], pendingSort: true };

      return updated.sort((a, b) => a.rank - b.rank);
    });
  };

  const togglePendingRemoval = async (id: number) => {
    let response = await catalogueService.canDeactive(id, tableName);
    if (typeof response === "boolean" && response) {

      setRecords((prev) =>
        prev.map((r) =>
          r.id === id ? { ...r, pendingRemoval: !r.pendingRemoval } : r
        )
      );
    } else {
      notificationFunctions.setErrorMessage(
        "Il campo selezionato è associato ad una segnalazione, per poter rimuovere il campo è necessario dissociarlo da tutte le segnalazioni"
      );
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>{title}</h5>
        <div>
          <CustomButtonSecondary
            onClick={() => (window.location.href = "/")}
            className="margin-right-5 col-auto"
            variant="contained"
          >
            Torna alla homepage
          </CustomButtonSecondary>

          {editable && (
            <CustomButton
              onClick={handleAddRecord}
              className="margin-right-5 col-auto"
              variant="contained"
            >
              Aggiungi record
            </CustomButton>
          )}

          {editable ? (
            <>
              <CustomButton
                onClick={handleSave}
                className="margin-right-5 col-auto"
                variant="contained"
              >
                Salva
              </CustomButton>
              <CustomButtonSecondary
                onClick={handleCancel}
                className="margin-right-5 col-auto"
                variant="contained"
              >
                Annulla
              </CustomButtonSecondary>
            </>
          ) : (
            <CustomButton
              onClick={() => setEditable(true)}
              className="margin-right-5 col-auto"
              variant="contained"
            >
              Abilita modifica
            </CustomButton>
          )}
        </div>
      </div>

      {records.length === 0 && (
        <p className="text-muted fst-italic">Nessun record disponibile.</p>
      )}

      {records
        .sort((a, b) => a.rank - b.rank)
        .map((record, idx, arr) => (
          <EditableRow
            key={record.id}
            record={record}
            editable={editable}
            onChange={(newValue) =>
              setRecords((prev) =>
                prev.map((r) =>
                  r.id === record.id ? { ...r, name: newValue } : r
                )
              )
            }
            onToggleRemove={() => togglePendingRemoval(record.id)}
            onMoveUp={() => moveRecord(record.id, "up")}
            onMoveDown={() => moveRecord(record.id, "down")}
            isFirst={idx === 0}
            isLast={idx === arr.length - 1}
          />
        ))}
    </div>
  );
};

export default ConfigTab;